import RippleWindow from './ripple/ripple-window.js';
import RippleTransforms from './ripple/ripple-transforms.js';

// Factory function - class is created when TarotEffect is available
function createRippleEffect(TarotEffect) {
	return class RippleEffect extends TarotEffect {
		static effectName = 'ripple';

		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: Infinity,
			loopBuffer: {
				left: 2,
				right: 2,
			},
		};

		constructor(ctx) {
			super(ctx);
			const _ = this;
			_.ctx = ctx;

			_.rippleWindow = new RippleWindow(ctx, _);
			_.rippleTransforms = new RippleTransforms(ctx, _);

			_.init();
		}

		init() {
			this.rippleWindow.init();
			this.rippleTransforms.init();
		}

		reInit() {
			this.rippleWindow.reInit();
			this.rippleTransforms.reInit();
		}

		/**
		 * Main render function called every animation frame
		 * Uses the frame-based architecture with dependency injection for utilities
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Array} frame.slides - Sorted slides array with calculated positions
		 * @param {Object} frame.widths - Layout measurements
		 * @param {Object} frame.state - Carousel state
		 * @param {Object} frame.animation - Animation data including trackPosition
		 * @param {Object} frame.transformPoints - Named position points
		 * @param {Object} utils - Frame utilities for position calculations
		 */
		render(frame, utils) {
			const _ = this;
			const { slides, widths, animation } = frame;

			// Set the max value for the ripple window based on slide widths
			_.rippleWindow.setMaxValue(widths.slide - widths.slideMin);

			// render some stuff
			_.renderTrackWidth(widths.track);
			_.renderTrackPosition(animation);
			_.renderSlideWidth(widths.slide);

			// Extract animation details from frame
			const trackPosition = animation.trackPosition;
			const trackDelta = animation.trackDelta || 0;
			const movementType = animation.movementType || 'jump';
			const progress = animation.progress || 1;

			// Add amount to sliding window but only with 'animate' or 'drag' movements
			if (
				movementType === 'scroll' ||
				((movementType === 'animate' || movementType === 'drag') && progress <= 1)
			) {
				_.rippleWindow.addAmount(trackDelta, trackPosition);
			}

			// Trigger settling animation when needed
			if (movementType === 'settle' || (movementType === 'animate' && progress > 0.75)) {
				_.rippleWindow.snapToValue(trackDelta, trackPosition);
			}

			// Get transform percentage from ripple window
			const transformPercent = _.rippleWindow.getPercent();

			// Slides are already sorted by renderIndex from frame engine
			let totalRemovedRight = 0;
			let slide;

			// First pass: calculate width adjustments and positions
			for (let i = 0, n = slides.length; i < n; ++i) {
				slide = slides[i];

				// clear left and right trimmed values
				slide._rightWidthTrimmed = 0;
				slide._leftWidthTrimmed = 0;

				// Apply ripple transforms ~~~~~~~~~~~~~~~~~~~~~~~~~~~
				// there's a whole bunch of magic happening here 🪄
				//  ╰( ͡◕ ͜ʖ ͡◕ )つ──~~⚝⚝⚝★★★🌟✨✨✨⚡⚡⚡💥💥💥🔥🔥🔥⚝:・ﾟ*~~~~~
				_.rippleTransforms.applyTransforms(slide, trackPosition, transformPercent, frame);

				// Calculate final display width
				const displayWidth = widths.slide - slide._rightWidthTrimmed - slide._leftWidthTrimmed;
				slide.style.transition = 'none';
				slide.style.width = `${displayWidth}px`;

				// Calculate position with accumulated right trim
				// subtract the totalRemovedRight from track postion to pull
				// the right slides towards the center
				slide._renderPosition = slide._trackPosition - totalRemovedRight;

				// Add to cumulative shift
				totalRemovedRight += slide._rightWidthTrimmed;
			}

			// Second pass: handle left offset and apply transforms
			let totalRemovedLeft = 0;
			for (let k = slides.length - 1; k >= 0; --k) {
				slide = slides[k];
				// Add left width removed to total removed left
				totalRemovedLeft += slide._leftWidthTrimmed;
				// add the totalRemovedLeft from track postion to pull
				// the left slides towards the center
				slide._renderPosition += totalRemovedLeft;
				// Update position on track for all slides
				slide.style.transform = `translateX(${slide._renderPosition}px)`;
			}
		}

		destroy() {
			super.destroy();
			const _ = this;

			// call destroy on subcomponents
			_.rippleWindow?.destroy();
			_.rippleTransforms?.destroy();

			// Clean up slide styles
			const slides = _.ctx.store.getSlides() || [];
			for (let i = 0, n = slides.length; i < n; ++i) {
				const slide = slides[i];
				if (!slide || !slide.style) continue;
				slide.removeAttribute('aria-hidden');
				slide.style.width = '';
				slide.style.transform = '';
				slide.style.transition = '';
			}

			// clear references for garbage collection
			_.rippleWindow = null;
			_.rippleTransforms = null;
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'ripple',
	factory: createRippleEffect,
});

export default createRippleEffect;
